import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import styles from './thumbnail-item.module.scss';

export interface ThumbnailItemProps {
  id?: string;
  className?: string;
  thumbnail: ReactElement<any>;
  isSelected?: boolean;
  state?: ThumbnailItemState;
  selectionState?: ThumbnailItemSelectionState;
  text?: string;
  textClassName?: string;
  thumbnailContainerClassName?: string;
  doScrollIntoView?: boolean;
  isThumbnailFull?: boolean;
  /**
   * only for outline selected border state
   */
  hasBorder?: boolean;
  onClick?(id?: string): void;
  onClickWhenDisabled?(id?: string): void;

  /**
   * only supported for thumbnail type icon
   */
  isDisabled?: boolean;
}

export enum ThumbnailItemState {
  DEFAULT = 'DEFUALT',
  ICON_ONLY = 'ICON_ONLY',
}

export enum ThumbnailItemSelectionState {
  BORDER = 'BORDER',
  OUTLINE = 'OUTLINE',
}

export function ThumbnailItem({
  onClick = (): void => {},
  onClickWhenDisabled = (): void => {},
  selectionState = ThumbnailItemSelectionState.BORDER,
  isSelected = false,
  state = ThumbnailItemState.DEFAULT,
  id = '',
  className = '',
  thumbnailContainerClassName = '',
  text = '',
  textClassName = '',
  doScrollIntoView = true,
  hasBorder = true,
  isDisabled = false,
  ...props
}: ThumbnailItemProps): ReactElement<any> {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (ref && ref.current && doScrollIntoView) {
      ref.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [isSelected]);

  const onClickHandler = (): void => {
    if (isDisabled) {
      onClickWhenDisabled(id);
      return;
    }
    onClick(id);
  };

  const getText = (): ReactElement<any> => {
    return (
      <Text
        val={text}
        size={TextSize.XXSMALL}
        bold={isSelected && !isDisabled}
        className={`${textClassName} ${styles.text} ${isDisabled ? 'color-extra-light' : isSelected ? 'color-primary-darker' : 'content-body'} spacing-m-t-1`}
      />
    );
  };

  const getSelectedThumbnailClasses = (): string => {
    if (selectionState === ThumbnailItemSelectionState.OUTLINE) {
      return `${styles.selectedOutline} ${hasBorder ? 'border-s-standard' : ''}`;
    }

    return 'border-s-primary';
  };

  const getSelectionClasses = (): string => {
    if (isDisabled) {
      return '';
    }
    if (state === ThumbnailItemState.DEFAULT) {
      return isSelected ? `${styles.selectedContainer} ${getSelectedThumbnailClasses()}` : hasBorder ? 'border-s-standard' : '';
    }
    return isSelected ? styles.selectedContainer : '';
  };

  const getThumbnail = (): ReactElement<any> => {
    return (
      <div
        className={`radius-8 ${
          state === ThumbnailItemState.DEFAULT ? styles.thumbnailContainer : styles.iconOnlyThumbnailContainer
        } ${thumbnailContainerClassName} ${getSelectionClasses()} flex-center`}
      >
        {props.thumbnail}
      </div>
    );
  };

  const getDefaultItem = (): ReactElement<any> => {
    return (
      <ClickableDiv
        key={id}
        onClick={onClickHandler}
        className={`${className} ${styles.container} ${selectionState === ThumbnailItemSelectionState.OUTLINE ? styles.outlineContainer : ''} flex-v-row flex-items-center`}
      >
        {getThumbnail()}
        {text ? getText() : null}
      </ClickableDiv>
    );
  };

  const getIconOnlyFlatState = (): ReactElement<any> => {
    return (
      <ClickableDiv
        key={id}
        onClick={onClickHandler}
        className={`radius-2 ${className} ${styles.container} ${styles.iconOnlyContainer} ${isDisabled ? 'color-extra-light' : ''} ${
          isSelected && !isDisabled ? styles.selectedIconOnlyContainer : ''
        } flex-v-row flex-items-center flex-content-center`}
      >
        {getThumbnail()}
        {text ? getText() : null}
      </ClickableDiv>
    );
  };

  const getThumbnailItem = (): ReactElement<any> => {
    if (state === ThumbnailItemState.DEFAULT) {
      return getDefaultItem();
    }

    return getIconOnlyFlatState();
  };

  return getThumbnailItem();
}
